import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, ArrowRight } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  extendNormalizedTableSchema,
  generateNormalizedTable,
  type ColumnSuggestion,
} from '../../services/data-integration-api';
import { toast } from 'sonner';

interface ExtendSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  normalizedTableId: string;
  sourceTableCatalogId: string;
  targetConnectionId: string;
  onSuccess?: () => void;
}

export default function ExtendSchemaDialog({
  open, onOpenChange, normalizedTableId, sourceTableCatalogId, targetConnectionId, onSuccess
}: ExtendSchemaDialogProps) {
  const [existingColumns, setExistingColumns] = useState<ColumnSuggestion[]>([]);
  const [newColumns, setNewColumns] = useState<Partial<ColumnSuggestion>[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchExistingSchema();
    } else {
      setNewColumns([]);
    }
  }, [open, normalizedTableId]);

  const fetchExistingSchema = async () => {
    setLoading(true);
    try {
      const result = await generateNormalizedTable({
        tableCatalogId: sourceTableCatalogId,
        targetConnectionId,
      });
      setExistingColumns(result.suggested_table.columns);
    } catch (e: any) {
      toast.error('获取现有架构失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setNewColumns([...newColumns, {
      target_column_name: '',
      target_data_type: 'varchar',
      target_column_length: 255,
      target_is_nullable: true,
      target_column_comment: '',
    }]);
  };

  const handleRemoveRow = (index: number) => {
    setNewColumns(newColumns.filter((_, i) => i !== index));
  };

  const handleUpdateRow = (index: number, updates: Partial<ColumnSuggestion>) => {
    const updated = [...newColumns];
    updated[index] = { ...updated[index], ...updates };
    setNewColumns(updated);
  };

  const handleSave = async () => {
    if (newColumns.length === 0) return;

    // 校验
    for (const col of newColumns) {
      if (!col.target_column_name) {
        toast.error('列名不能为空');
        return;
      }
      if (existingColumns.some(ec => ec.target_column_name === col.target_column_name)) {
        toast.error(`列名 "${col.target_column_name}" 已存在`);
        return;
      }
    }

    setSaving(true);
    try {
      await extendNormalizedTableSchema({
        normalizedTableId,
        newColumns: newColumns as ColumnSuggestion[],
      });
      toast.success('架构扩展成功，物理表已更新');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (e: any) {
      toast.error('架构扩展失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>扩展目标表架构 (ALTER TABLE)</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          <div className="mb-6">
            <h4 className="text-sm font-medium mb-2 text-muted-foreground">现存字段 ({existingColumns.length})</h4>
            <div className="border rounded-md overflow-hidden bg-muted/30">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="h-9">目标字段名</TableHead>
                    <TableHead className="h-9">类型</TableHead>
                    <TableHead className="h-9">备注</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {existingColumns.map(col => (
                    <TableRow key={col.target_column_name}>
                      <TableCell className="py-1.5 font-mono text-xs">{col.target_column_name}</TableCell>
                      <TableCell className="py-1.5 text-xs">{col.target_data_type}</TableCell>
                      <TableCell className="py-1.5 text-xs text-muted-foreground">{col.target_column_comment || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">新增扩展字段</h4>
              <Button variant="outline" size="sm" onClick={handleAddRow}>
                <Plus className="h-4 w-4 mr-1" /> 添加字段
              </Button>
            </div>
            
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>目标字段名</TableHead>
                  <TableHead className="w-[150px]">数据类型</TableHead>
                  <TableHead className="w-[100px]">长度/精度</TableHead>
                  <TableHead>备注</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newColumns.map((col, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input
                        value={col.target_column_name}
                        onChange={e => handleUpdateRow(idx, { target_column_name: e.target.value })}
                        placeholder="new_column_name"
                        className="font-mono h-8 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={col.target_data_type}
                        onValueChange={v => handleUpdateRow(idx, { target_data_type: v })}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="varchar">varchar</SelectItem>
                          <SelectItem value="integer">integer</SelectItem>
                          <SelectItem value="bigint">bigint</SelectItem>
                          <SelectItem value="decimal">decimal</SelectItem>
                          <SelectItem value="boolean">boolean</SelectItem>
                          <SelectItem value="text">text</SelectItem>
                          <SelectItem value="timestamp">timestamp</SelectItem>
                          <SelectItem value="jsonb">jsonb</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={col.target_column_length || ''}
                        onChange={e => handleUpdateRow(idx, { target_column_length: parseInt(e.target.value) || undefined })}
                        className="h-8 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={col.target_column_comment}
                        onChange={e => handleUpdateRow(idx, { target_column_comment: e.target.value })}
                        placeholder="字段注释"
                        className="h-8 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveRow(idx)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {newColumns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground italic">
                      点击右上角“添加字段”按钮开始定义
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={saving || newColumns.length === 0}>
            {saving ? '正在应用变更...' : (
              <>
                <Save className="h-4 w-4 mr-2" /> 应用架构变更
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
